import { Platform } from 'react-native'
import { Href, useRouter } from 'expo-router'
import { useStore } from '@/models'
import { auth0Config } from '@/config/auth0'
import { useInitialLinkingStore } from '@/hooks/useInitialLinkingStore'
import * as WebBrowser from 'expo-web-browser'
import { useQuery } from '@tanstack/react-query'
import { onSignInSuccess } from '@/screens/sign-up/onSignInSuccess'
import { useIsEmbeddedSegment } from '@/hooks/useIsEmbeddedSegment'
import { Auth0Client } from '@auth0/auth0-spa-js'
import * as AuthSession from 'expo-auth-session'
import { useSessionStore } from '@/models/session/useSessionStore'

WebBrowser.maybeCompleteAuthSession()

const isWeb = Platform.OS === 'web'

// Create singleton Auth0 client
let auth0ClientInstance: Auth0Client | null = null

export function getAuth0Client() {
  if (auth0ClientInstance) {
    return auth0ClientInstance
  }

  const redirectUri = AuthSession.makeRedirectUri({
    native: Platform.OS === 'web' ? window.location.href : undefined,
    path: '/login'
  })

  auth0ClientInstance = new Auth0Client({
    domain: auth0Config.discoveryUrl,
    clientId: auth0Config.clientId,
    authorizationParams: {
      redirect_uri: redirectUri,
      audience: auth0Config.audience,
      scope: auth0Config.scopes.join(' ')
    },
    // Web uses session cookies via SPA SDK, not refresh tokens
    // Native apps use actual refresh tokens handled elsewhere
    useRefreshTokens: false,
    cacheLocation: 'localstorage'
  })

  return auth0ClientInstance
}

async function attemptSilentAuth(auth0Client: Auth0Client) {
  // Check if user is already authenticated (uses hidden iframe, no popup)
  const isAuthenticated = await auth0Client.isAuthenticated()

  if (!isAuthenticated) {
    // Try to get token silently (uses hidden iframe with prompt=none)
    try {
      await auth0Client.getTokenSilently({
        authorizationParams: {
          audience: auth0Config.audience,
          scope: auth0Config.scopes.join(' ')
        },
        cacheMode: 'off' // Always check with Auth0
      })
    } catch (silentError: unknown) {
      // login_required, consent_required, or interaction_required means no session
      // Silent auth works via session cookies, not refresh tokens
      const error = silentError as { error?: string }
      if (
        error.error === 'login_required' ||
        error.error === 'consent_required' ||
        error.error === 'interaction_required'
      ) {
        // Silent authentication not possible (no existing session)
        return null
      }
      // Any other error should return null gracefully
      return null
    }
  }

  // Get the tokens from Auth0 client
  const accessToken = await auth0Client.getTokenSilently({
    cacheMode: 'off',
    authorizationParams: {
      audience: auth0Config.audience,
      scope: auth0Config.scopes.join(' ')
    }
  })

  const idTokenClaims = await auth0Client.getIdTokenClaims()

  if (!accessToken) {
    return null
  }

  if (!idTokenClaims) {
    return null
  }

  // Create a complete token config
  return {
    accessToken,
    tokenConfig: {
      accessToken: accessToken,
      idToken: idTokenClaims.__raw,
      tokenType: 'bearer' as const,
      expiresIn: idTokenClaims.exp ? idTokenClaims.exp - Math.floor(Date.now() / 1000) : 86400,
      issuedAt: Math.floor(Date.now() / 1000),
      scope: auth0Config.scopes.join(' '),
      state: '',
      // Flag to indicate this uses Auth0 SPA SDK for refresh
      refreshToken: 'managed_by_auth0_spa_sdk'
    } as AuthSession.TokenResponseConfig
  }
}

export function useSilentSignIn() {
  const router = useRouter()
  const rootStore = useStore()
  const isEmbeddedScreen = useIsEmbeddedSegment()

  const silentSignIn = useQuery({
    queryKey: ['silentSignIn'] as const,
    retry: false,
    enabled: Platform.OS === 'web' && !useSessionStore.getState().getAccessToken(),
    queryFn: async () => {
      const existingToken = useSessionStore.getState().getAccessToken()
      if (existingToken) {
        return existingToken
      }

      try {
        const auth0Client = getAuth0Client()
        const result = await attemptSilentAuth(auth0Client)

        if (!result) {
          return null
        }

        const { accessToken, tokenConfig } = result

        useSessionStore.getState().setTokenConfig(tokenConfig)
        await rootStore?.session.fetchMe()
        rootStore?.session.mergeAnonymousUserId()
        onSignInSuccess()

        // Redirect to initial url or root
        const initialUrl = useInitialLinkingStore.getState().url

        if (!isEmbeddedScreen) {
          if (initialUrl) {
            const fullHref = initialUrl.search
              ? `${initialUrl.pathname}${initialUrl.search}`
              : initialUrl.pathname
            rootStore?.analytics.setEvent('Deep Link Consumed', { url: fullHref, source: 'SilentSignIn' })
            router.replace(fullHref as Href)
          } else {
            router.replace('/')
          }
        }

        return accessToken
      } catch (error) {
        // Silently handle errors without logging in production
        if (__DEV__) {
          console.log('Silent sign-in failed:', error)
        }
        return null
      }
    }
  })

  return silentSignIn
}
